import { useMemo } from 'react'
import { toCurrency } from '@/utils/currency.utils'
import { getSubtotals } from '@/utils/cart.utils'

type Props = {
  cart: Transformed.Cart,
  giftCardBalance: readonly Transformed.GiftCardBalance[]
  localLineItems: Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
}

type ReturnType = {
  /** The final total minus discounts */
  totalWithDiscount: string,
  /** The total minus taxes */
  totalMinusTaxes: string
  /** all discounts applied to the cart */
  discount: number
  /** The final total */
  total: number,
  /** The final subtotal */
  subTotal: number,
  /** The total gift cards applied */
  giftCardAppliedAmount: number
}

/**
 * A React hook that calculates the total with discount for a cart.
 * This hook takes in cart, localLineItems, lineItemsToDelete, and giftCardBalance as props,
 * and returns an object with various calculated values including the calculated cart total with discount,
 * all discounts applied to the cart, the final total, the final subtotal, and the total gift cards applied.
 * @param {Props} props - Props for the hook.
 * @returns {ReturnType} - Return type for the hook.
 */
const useGetTotalWithDiscount = (
  {
    cart,
    giftCardBalance,
    localLineItems = [],
    lineItemsToDelete = [],
  }: Props,
): ReturnType => {
  const discount = cart.promoCodeDiscount || 0

  const total = useMemo(
    () => getSubtotals(cart.grandTotal || null, [...localLineItems], lineItemsToDelete),
    [cart.grandTotal, localLineItems, lineItemsToDelete],
  )

  const subTotal = useMemo(
    () => getSubtotals(cart.subTotal || null, [...localLineItems], lineItemsToDelete),
    [cart.subTotal, localLineItems, lineItemsToDelete],
  )

  const giftCardAppliedAmount = useMemo(
    () => giftCardBalance.reduce((acc, cv) => acc + cv.balance, 0),
    [giftCardBalance],
  )

  const totalWithDiscount = useMemo(
    () => toCurrency(total - giftCardAppliedAmount, 2),
    [total, giftCardAppliedAmount],
  )

  const totalMinusTaxes = useMemo(
    () => toCurrency(total - cart.taxesTotal, 2),
    [total, cart.taxesTotal],
  )

  return { totalWithDiscount, totalMinusTaxes, discount, total, subTotal, giftCardAppliedAmount }
}

export default useGetTotalWithDiscount
